import { extendZodWithOpenApi } from '@anatine/zod-openapi'
import z from 'zod'
import { Brand, createSortQuerySchema } from '../../shared'
import { FeedingPlanType } from '../common'
import { ProductType } from '../product/schemas'
import {
  CALCULATION_STRATEGY,
  DiscountInterval,
  DiscountScheduleApplicationFilterType,
  DiscountScheduleApplicationType,
  FEES,
  PromotionType,
  PromotionUsageContext,
  PromotionUsageType,
} from './types'

extendZodWithOpenApi(z)

export const PricingBaseSchema = z.object({
  subscriptionId: z.string().uuid().nullable().optional(),
  orderId: z.string().optional().nullable(),
  calculationType: z.nativeEnum(CALCULATION_STRATEGY),
  // at time of writing brand is only used when providing a `promotionCode` to identify which brand the promotion belongs to
  // will default to LYKA if not provided (zod default is not used since only z.infer is used instead of using z.input for the input types)
  brand: z.nativeEnum(Brand).optional(),

  discounts: z
    .object({
      discountId: z.string().uuid(),
      orderPosition: z.number().int().nonnegative(),
    })
    .array()
    .optional(),

  promotionCode: z.string().max(250).optional(),
})

export type PricingBaseSchema = z.infer<typeof PricingBaseSchema>

export const FeesSchema = z.object({
  type: z.nativeEnum(FEES),
  postCode: z.string(),

  // non-discounted price
  price: z.number().int().nonnegative(),

  // TODO: Dan make non-optional before discounts goes live
  discountedPrice: z.number().int().nonnegative().optional(),
})

export type FeesSchema = z.infer<typeof FeesSchema>

export const ItemPriceSchema = z.object({
  appliedDiscountId: z
    .string()
    .uuid()
    .optional()
    .describe('The id of the highest-value discount applied to this line item'),
  appliedDiscountApplicationId: z
    .string()
    .uuid()
    .optional()
    .describe('The schedule application id of the highest-value discount applied to this line item'),
  discountedQuantity: z.number().int().optional(),

  totalPrice: z.number().int().nonnegative().describe('The original non-discounted total price (unitPrice * quantity)'),
  discountTotal: z.number().int().nonnegative().optional().describe('The discount portion of the total price'),
  totalCharge: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe(
      'The total amount charged after discount. Always populated in output; equals totalPrice when no discount applies',
    ),

  unitPrice: z.number().int().nonnegative().describe('The original non-discounted per unit price'),
  discountPerItem: z.number().int().nonnegative().optional().describe('The discount portion of the per unit price'),
  unitChargePrice: z
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe(
      'The per unit amount charged after discount. Always populated in output; equals unitPrice when no discount applies',
    ),
})

export type ItemPriceSchema = z.infer<typeof ItemPriceSchema>

export const ItemsSchema = z.object({
  sku: z.string().optional(),
  quantity: z.number(),
  basePrice: z.number().int().nonnegative(),
  discountId: z.string().uuid().optional(),
  id: z.string().uuid().optional(),
  productId: z.string().uuid().optional(),
  productVariantId: z.string().uuid().optional(),
  productType: z.nativeEnum(ProductType).optional(),

  // TODO: Dan when going-live make this non-optional
  price: ItemPriceSchema.optional(),
})

export type ItemsSchema = z.infer<typeof ItemsSchema>

export const ExtrasSchema = ItemsSchema.omit({ id: true }).extend({
  lineItemId: z.string().uuid().optional(),
  productId: z.string().uuid(),
  productVariantId: z.string().uuid(),
  productType: z.nativeEnum(ProductType),
})

export type ExtrasSchema = z.infer<typeof ExtrasSchema>

export const PlansSchema = z.object({
  id: z.string().uuid(),
  // animalId is optional for shared feeding plans where SKUs are pooled across animals
  animalId: z.string().uuid().optional(),
  planType: z.nativeEnum(FeedingPlanType).default(FeedingPlanType.full_bowl),
  baseCadenceInWeeks: z.number(),
  customCadenceInWeeks: z.number().optional().nullable(),
  dailyServingSize: z.number(),
  items: z.array(ItemsSchema),
  customShippingFees: z.number().optional().nullable(),
})

export type PlansSchema = z.infer<typeof PlansSchema>

export const GetPricingInputSchema = PricingBaseSchema.extend({
  priceVersion: z.number().int().positive().optional().nullable(),
  fees: z.array(FeesSchema),
  plans: z.array(PlansSchema).default([]),
  extras: z.array(ExtrasSchema).default([]),
})

export type GetPricingInputSchema = z.infer<typeof GetPricingInputSchema>

export const PricingPlanSchema = PlansSchema.extend({
  // TODO: Remove these columns when going live
  totalPrice: z.number().int().nonnegative(),
  dailyPrice: z.number().int().nonnegative(),
  pricePerPouch: z.number().int().nonnegative(),

  // TODO: Dan make this non-optional and make the totalPrice, dailyPrice and pricePerPouch required
  price: z
    .object({
      priceTotal: z.number().int().nonnegative(),
      discountedPriceTotal: z.number().int().nonnegative().optional(),
      dailyTotal: z.number().int().nonnegative(),
      discountedDailyTotal: z.number().int().nonnegative().optional(),
      pricePerPouch: z.number().int().nonnegative(),
      discountedPricePerPouch: z.number().int().nonnegative().optional(),
    })
    .optional(),
})

export type PricingPlanSchema = z.infer<typeof PricingPlanSchema>

export const PricingSchema = PricingBaseSchema.extend({
  plans: z.array(PricingPlanSchema).default([]),
  extras: z.array(ExtrasSchema).default([]),

  // Meal-level (global) discount applied across all meal plans. Populated when an
  // order discount applies to the meal plans.
  discountId: z.string().uuid().optional().describe('The discount applied on the order'),
  appliedDiscountId: z.string().uuid().optional().describe('The id of the discount applied on the order'),
  appliedDiscountApplicationId: z.string().uuid().optional().describe('The schedule application id applied'),

  // TODO: Dan make this non-optional when discounts goes live
  price: z.object({
    planTotal: z.number().int().nonnegative(),
    discountedPlanTotal: z.number().int().nonnegative().optional(),
    extraTotal: z.number().int().nonnegative(),
    discountedExtraTotal: z.number().int().nonnegative().optional(),
    priceTotal: z.number().int().nonnegative(),
    discountedPriceTotal: z.number().int().nonnegative().optional(),
  }),
})

export type PricingSchema = z.infer<typeof PricingSchema>

const PaginationResponse = z.object({
  totalRecords: z.number(),
  totalPages: z.number(),
  page: z.number(),
  perPage: z.number(),
})

const PaginationRequest = z.object({
  page: z.coerce.number().default(1),
  perPage: z.coerce.number().default(100),
  orderBy: z.enum(['name', 'createdAt', 'updatedAt']).optional(),
  orderByDirection: z.enum(['ASC', 'DESC']).optional(),
})

const DateSchema = z
  .union([z.string().date(), z.date()])
  .transform((d) => (d instanceof Date ? d.toISOString().split('T')[0] : d))
  .optional()
  .nullable()

const DateTimeSchema = z
  .preprocess((value) => {
    if (value === undefined || value === null || value instanceof Date) {
      return value
    }

    // if provided value is a string or number type, try parse as date
    if (typeof value === 'string' || typeof value === 'number') {
      const date = new Date(value)

      return Number.isNaN(date.getTime()) ? undefined : date
    }

    return undefined
  }, z.date())
  .transform((date: Date) => date.toISOString())

const QueryBooleanSchema = z.preprocess(
  (val) => val === '1' || val === 1 || val === true || val === 'true',
  z.boolean(),
)

function QueryCsvSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return z.preprocess((val) => {
    if (Array.isArray(val)) return val
    if (typeof val === 'string')
      return val
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    if (val === undefined || val === null) return undefined
    return [val]
  }, z.array(itemSchema).optional())
}

export type GenericCrudPathParams = z.infer<typeof GenericCrudPathParams>

export const GenericCrudPathParams = z.object({
  uuid: z.string().uuid(),
})

export type GenericCrudPostResponse = z.infer<typeof GenericCrudPostResponse>

export const GenericCrudPostResponse = z.object({
  id: z.string().uuid(),
})

export type GenericCrudDeleteRequest = z.infer<typeof GenericCrudDeleteRequest>

export const GenericCrudDeleteRequest = z.object({
  deletedBy: z.string().uuid(),
})

export type GenericCrudDeleteResponse = z.infer<typeof GenericCrudDeleteResponse>

export const GenericCrudDeleteResponse = z.object({
  message: z.string(),
})

export type DiscountCategorySchema = z.infer<typeof DiscountCategorySchema>

export const DiscountCategorySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  userSelectable: z.boolean().describe('Is the category user selectable from UIs like CCA'),
  createdAt: DateSchema,
  updatedAt: DateSchema,
  deletedAt: DateSchema,
})

export type DiscountCategoryGetResponse = z.infer<typeof DiscountCategoryGetResponse>

export const DiscountCategoryGetResponse = PaginationResponse.extend({
  results: z.array(DiscountCategorySchema),
})

export type DiscountCategoryGetRequest = z.infer<typeof DiscountCategoryGetRequest>

export const DiscountCategoryGetRequest = PaginationRequest.extend({
  name: z.string().optional(),
  includeDeleted: QueryBooleanSchema.optional().describe('Include soft-deleted categories'),
})

const DiscountCategoryBase = z.object({
  name: z.string().max(250),
  userSelectable: z.boolean().optional().describe('Is the category user selectable from UIs like CCA'),
})

export type DiscountCategoryPostRequest = z.infer<typeof DiscountCategoryPostRequest>

export const DiscountCategoryPostRequest = DiscountCategoryBase.extend({})

export type DiscountCategoryPatchRequest = z.infer<typeof DiscountCategoryPatchRequest>

export const DiscountCategoryPatchRequest = DiscountCategoryBase.extend({})

const FilterBase = z.object({
  quantity: z
    .number()
    .int()
    .min(1)
    .optional()
    .describe('Specify how many items of the type can be discounted. Leave empty for all product quantities.'),
})

const FilterAllItems = FilterBase.extend({
  type: z.literal(DiscountScheduleApplicationFilterType.ALL_ITEMS),
})

const FilterProduct = FilterBase.extend({
  type: z.literal(DiscountScheduleApplicationFilterType.PRODUCT),
  productId: z.string().uuid().describe('Required when type set to product.'),
})

const FilterProductVariant = FilterBase.extend({
  type: z.literal(DiscountScheduleApplicationFilterType.PRODUCT_VARIANT),
  productVariantId: z.string().uuid().describe('Required when type set to product-variant.'),
})

const FilterProductType = FilterBase.extend({
  type: z.literal(DiscountScheduleApplicationFilterType.PRODUCT_TYPE),
  productType: z.nativeEnum(ProductType).describe('Required when type set to product-type.'),
})

export type DiscountPostRequest = z.infer<typeof DiscountPostRequest>

export const DiscountPostRequest = z.object({
  id: z
    .string()
    .uuid()
    .optional()
    .describe('This enables overriding the discount id. It should be used for migration purposes only'),
  categoryId: z.string().uuid(),
  name: z.string(),
  description: z.string().max(800).optional(),
  deletedAt: z
    .string()
    .datetime()
    .optional()
    .nullable()
    .describe('Soft-delete the discount at creation. Should only be used for migration purposes.'),
  interval: z
    .nativeEnum(DiscountInterval)
    .describe(
      'Type of discount. Once-off for one off discounts, Recurring for discounts that are on-going and Hybrid for a combination of both. If specifying Hybrid, exactly one Schedule must be marked recurring.',
    ),
  currencyCode: z
    .enum(['AUD'])
    .describe(
      'Currency code of dollar values to be applied. At time of writing only Australian Dollar is support with room for expansion. You can safely use AUD.',
    ),
  expireAt: DateTimeSchema.nullable().optional(),
  timezone: z.string().max(50).default('Australia/Sydney'),
  createdBy: z.string().uuid(),
  schedules: z
    .array(
      z.object({
        orderPosition: z
          .number()
          .int()
          .nonnegative()
          .describe(
            'What order position is it? This is zero-index, next box discounts set this to 0. If second order discount, set this to 1.',
          ),
        recurring: z
          .boolean()
          .default(false)
          .describe('Should only exist for one schedule if the discount is a Hybrid type.'),
        applications: z
          .array(
            z.object({
              type: z
                .nativeEnum(DiscountScheduleApplicationType)
                .describe('What type of discount are applying? Locked at percentage for MVP.'),
              value: z
                .number()
                .int()
                .nonnegative()
                .describe(
                  'If type is for a percentage. This should represent the discount percentage; e.g. 50 for 50%',
                ),
              filters: z
                .array(
                  z.discriminatedUnion('type', [
                    FilterAllItems,
                    FilterProduct,
                    FilterProductType,
                    FilterProductVariant,
                  ]),
                )
                .min(1),
            }),
          )
          .min(1),
      }),
    )
    .min(1),
})

export type DiscountSchema = z.infer<typeof DiscountSchema>

export const DiscountSchema = z.object({
  id: z.string().uuid(),
  category: z.object({
    id: z.string().uuid(),
    name: z.string(),
  }),
  name: z.string(),
  description: z.string().optional(),
  interval: z.nativeEnum(DiscountInterval),
  currencyCode: z.string(),
  createdBy: z.string().uuid().nullable().default(null),
  updatedBy: z.string().uuid().nullable().default(null),
  createdAt: DateSchema,
  updatedAt: DateSchema,
  deletedAt: DateSchema,
  disabledAt: DateSchema,
  schedules: z.array(
    z.object({
      id: z.string().uuid(),
      orderPosition: z.number().int().nonnegative(),
      recurring: z.boolean(),
      applications: z.array(
        z.object({
          id: z.string().uuid(),
          type: z.nativeEnum(DiscountScheduleApplicationType),
          value: z.number().int().nonnegative(),
          filters: z.array(
            z.object({
              id: z.string().uuid(),
              type: z.nativeEnum(DiscountScheduleApplicationFilterType),
              productId: z.string().uuid().nullable().optional(),
              productVariantId: z.string().uuid().nullable().optional(),
              productType: z.nativeEnum(ProductType).nullable().optional(),
              quantity: z.number().int().nonnegative().nullable().optional(),
            }),
          ),
        }),
      ),
    }),
  ),
})

export type DiscountGetRequest = z.infer<typeof DiscountGetRequest>

export const DiscountGetRequest = PaginationRequest.extend({
  orderBy: z.enum(['name', 'category', 'interval', 'createdAt', 'updatedAt']).optional(),
  categoryId: z.string().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  interval: z.nativeEnum(DiscountInterval).optional().describe('Interval of the discount'),
  currencyCode: z.string().optional(),
  multipleOrder: QueryBooleanSchema.optional().describe('Is the discount for multiple orders (multi-schedule)'),
  includeDeleted: QueryBooleanSchema.optional().describe('Include soft-deleted discounts'),
  includeNonUserSelectable: QueryBooleanSchema.optional().describe(
    'Include discounts with a category that is not user selectable (e.g. checkout discounts)',
  ),
  applicationType: QueryCsvSchema(z.nativeEnum(DiscountScheduleApplicationType)).describe(
    'Filter by application type (percentage, dollar). Supports CSV.',
  ),
  applicationValue: z.coerce
    .number()
    .int()
    .nonnegative()
    .optional()
    .describe('Filter by exact application value (e.g. 100 for 100%)'),
  filterType: QueryCsvSchema(z.nativeEnum(DiscountScheduleApplicationFilterType)).describe(
    'Filter by schedule application filter type (all-items, product, product-variant, product-type). Supports CSV.',
  ),
  filterProductType: QueryCsvSchema(z.nativeEnum(ProductType)).describe(
    'Filter by product type on schedule application filters (meal, treat, supplement, etc.). Supports CSV.',
  ),
})

export type DiscountGetResponse = z.infer<typeof DiscountGetResponse>

export const DiscountGetResponse = PaginationResponse.extend({
  results: z.array(DiscountSchema),
})

export const DiscountGetBatchRequestSchema = z.object({
  discountIds: z.array(z.string().uuid()).describe('Array of discount ids'),
})

export type DiscountGetBatchRequest = z.infer<typeof DiscountGetBatchRequestSchema>

export const DiscountGetBatchResponseSchema = z.object({
  discounts: z.array(
    z.object({
      id: z.string().uuid(),
      interval: z.nativeEnum(DiscountInterval),
    }),
  ),
})

export type DiscountGetBatchResponse = z.infer<typeof DiscountGetBatchResponseSchema>

export type DiscountPatchRequest = z.infer<typeof DiscountPatchRequest>

export const DiscountPatchRequest = z.object({
  categoryId: z.string().uuid().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  disable: z
    .boolean()
    .optional()
    .describe('Marks discount as disabled/enabled. This does not soft-delete the discount.'),
  updatedBy: z.string().uuid().nullable().default(null),
})

export type PromotionUserSchema = z.infer<typeof PromotionUserSchema>

export const PromotionUserSchema = z.object({
  id: z.string().uuid(),
  promotionId: z.string().uuid().optional(),
  discountId: z.string().uuid().nullable(),
  type: z.nativeEnum(PromotionUsageType),
  userId: z.string().uuid(),
  context: z.nativeEnum(PromotionUsageContext).nullable(),
  appliedAt: DateSchema.nullable(),
  createdAt: DateSchema,
  updatedAt: DateSchema,
  deletedAt: DateSchema.nullable(),
})

export type PromotionSchema = z.infer<typeof PromotionSchema>

export const PromotionSchema = z.object({
  id: z.string().uuid().optional(),
  discountId: z.string().uuid().optional(),
  categoryId: z.string().uuid(),
  referrerUserId: z.string().uuid().optional(),
  rafCampaignId: z.string().uuid().nullable().optional(),
  type: z.nativeEnum(PromotionType),
  brand: z.nativeEnum(Brand),
  value: z.string().min(1).max(250),
  name: z.string().trim().min(1).max(250),
  maxUses: z.number().min(1).nullable().optional(),
  createdBy: z.string().uuid(),
  updatedBy: z.string().uuid(),
  timezone: z.string(),
  activeAt: DateTimeSchema.nullable().optional(),
  expireAt: DateTimeSchema.nullable().optional(),
  disabledAt: DateTimeSchema.nullable().optional(),
  createdAt: DateTimeSchema.nullable().optional(),
  updatedAt: DateTimeSchema.nullable().optional(),
  deletedAt: DateSchema.nullable().readonly(),
  users: z.array(PromotionUserSchema).optional().describe('Who used this promotion'),
})

/**
 * Whitelist of sortable columns for the promotions list.
 * `disabled` is a virtual field (derived from `disabledAt`): `disabled asc` = active-first.
 */
export const PROMOTION_SORT_COLUMNS = ['disabled', 'name', 'value', 'createdAt', 'updatedAt'] as const

export type PromotionGetRequest = z.infer<typeof PromotionGetRequest>

export const PromotionGetRequest = PaginationRequest.omit({ orderByDirection: true }).extend({
  brand: z.nativeEnum(Brand).optional(),
  discountId: z.string().uuid().optional(),
  referrerUserId: z.string().uuid().optional().describe('Owner of this promotion'),
  type: z.nativeEnum(PromotionType).optional(),
  value: z.string().optional().describe('Promotion code value'),
  maxUses: z.coerce.number().optional().describe('Max uses of this promotion'),
  timezone: z.string().max(50).optional(),
  activeAt: z.coerce.date().optional(),
  expireAt: z.coerce.date().optional(),
  createAt: z.coerce.date().optional(),
  createdBy: z.string().uuid().optional().describe('User ID of whomever created the promotion'),
  includeDisabled: QueryBooleanSchema.optional(),
  includeUsers: QueryBooleanSchema.optional(),
  orderBy: createSortQuerySchema(PROMOTION_SORT_COLUMNS).describe('AIP-132 sort, e.g. "disabled asc, name". Default direction ASC.'),
})

export type PromotionGetResponse = z.infer<typeof PromotionGetResponse>

export const PromotionGetResponse = PaginationResponse.extend({
  results: z.array(PromotionSchema),
})

export type PromotionByCodePathParams = z.infer<typeof PromotionByCodePathParams>

export const PromotionByCodePathParams = z.object({
  brand: z.nativeEnum(Brand),
  code: z.coerce.string().min(1),
})

export type PromotionByIdPathParams = z.infer<typeof PromotionByIdPathParams>

export const PromotionByIdPathParams = z.object({
  id: z.string().uuid(),
})

export const ReferrerPromotionPostRequest = z.object({
  userId: z.string().uuid(),
  value: z.string(),
  brand: z.nativeEnum(Brand),
  rafCampaignId: z.string().uuid().nullable().optional(),
})

export type ReferrerPromotionPostRequest = z.infer<typeof ReferrerPromotionPostRequest>

export type PromotionPostRequest = z.infer<typeof PromotionPostRequest>

export const PromotionPostRequest = PromotionSchema.omit({
  users: true,
  name: true,
}).extend({
  // TODO (R2-4884) remove optional during release/promotions-admin ff removal.
  name: z.string().trim().min(1).max(250).optional(),
})

export type PromotionPatchRequest = z.infer<typeof PromotionPatchRequest>

export const PromotionPatchRequest = z
  .object({
    discountId: z.string().uuid().optional(),
    categoryId: z.string().uuid().optional(),
    referrerUserId: z.string().uuid().optional(),
    type: z.nativeEnum(PromotionType).optional(),
    value: z.string().max(250).optional(),
    name: z.string().trim().min(1).max(250).optional(),
    maxUses: z.number().min(1).nullable().optional(),
    timezone: z.string().optional(),
    activeAt: DateTimeSchema.nullable().optional(),
    expireAt: DateTimeSchema.nullable().optional(),
    disable: z
      .boolean()
      .optional()
      .describe('Marks promotion as disabled/enabled. This does not soft-delete the promotion.'),
    updatedBy: z.string().uuid(),
  })
  .strict()

const AttributionPostBase = z.object({
  userId: z.string().uuid().describe('Which user are we associating with the promotion (discount/referral)'),
  promotionId: z.string().uuid(),
})

const AttributionPostDiscount = AttributionPostBase.extend({
  type: z.literal(PromotionUsageType.DISCOUNT),
})

const AttributionPostReferral = AttributionPostBase.extend({
  type: z.literal(PromotionUsageType.REFERRAL),
  context: z.nativeEnum(PromotionUsageContext),
})

export type AttributionPostRequest = z.infer<typeof AttributionPostRequest>

export const AttributionPostRequest = z.discriminatedUnion('type', [AttributionPostDiscount, AttributionPostReferral])

export type AttributionPostResponse = z.infer<typeof AttributionPostResponse>

export const AttributionPostResponse = z.object({
  promotionUserId: z.string().uuid().describe('The ID of the promotion usage'),
})

export type PromotionValidateRequest = z.infer<typeof PromotionValidateRequest>

export const PromotionValidateRequest = z.object({
  brand: z.nativeEnum(Brand),
  promotionCode: z.string().min(1),
  usageType: z.nativeEnum(PromotionUsageType),
})

export type PromotionValidateResponse = z.infer<typeof PromotionValidateResponse>

export const PromotionValidateResponse = z.object({
  promotionId: z.string().uuid(),
  discountId: z.string().uuid().nullable(),
})

// ─── Bulk Promotion Upsert  (RAF Migration) ──────────────────────────────────────────────────
export type BulkPromotionPostRequest = z.infer<typeof BulkPromotionPostRequest>

export const BulkPromotionPostRequest = z.object({
  promotions: z.array(PromotionPostRequest).max(1000),
})

export type BulkPromotionPostResponse = z.infer<typeof BulkPromotionPostResponse>

export const BulkPromotionPostResponse = z.object({
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  errors: z.array(
    z.object({
      value: z.string(),
      error: z.string(),
    }),
  ),
})

// ─── RAF Campaigns ────────────────────────────────────────────────────────────
export const RafCampaignReferrerRewardItemSchema = z.object({
  id: z.string().uuid(),
  productVariantId: z.string().uuid(),
  quantity: z.number().int().positive(),
})

export const RafCampaignReferrerRewardSchema = z.object({
  id: z.string().uuid(),
  referralCountRequired: z.number().int().positive(),
  /**
   * Last lifetime referral position at which this reward can still be claimed.
   * Null or omitted means it never expires.
   */
  maxReferralPosition: z.number().int().positive().nullable().optional(),
  discountId: z.string().uuid(),
  items: z.array(RafCampaignReferrerRewardItemSchema),
})

export const RafCampaignSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(60),
  description: z.string().nullable().optional(),
  startDate: z.string().describe('ISO date string (YYYY-MM-DD)'),
  endDate: z.string().describe('ISO date string (YYYY-MM-DD)'),
  brand: z.nativeEnum(Brand),
  referrerDiscountId: z.string().uuid().nullable().optional(),
  refereeDiscountId: z.string().uuid().nullable().optional(),
  laravelId: z.number().int().nullable().optional(),
  createdAt: DateSchema.readonly(),
  updatedAt: DateSchema.readonly(),
  deletedAt: DateSchema.nullable().readonly(),
  referrerRewards: z.array(RafCampaignReferrerRewardSchema).optional(),
})

export type RafCampaignSchema = z.infer<typeof RafCampaignSchema>

export const RafCampaignReferrerRewardItemPostRequest = z.object({
  productVariantId: z.string().uuid(),
  quantity: z.number().int().positive(),
})

export type RafCampaignReferrerRewardItemPostRequest = z.infer<typeof RafCampaignReferrerRewardItemPostRequest>

export const RafCampaignReferrerRewardPostRequest = z.object({
  referralCountRequired: z.number().int().positive(),
  /**
   * Last lifetime referral position at which this reward can still be claimed.
   * Null or omitted means it never expires.
   */
  maxReferralPosition: z.number().int().positive().nullable().optional(),
  discountId: z.string().uuid(),
  items: z.array(RafCampaignReferrerRewardItemPostRequest),
})

export type RafCampaignReferrerRewardPostRequest = z.infer<typeof RafCampaignReferrerRewardPostRequest>

export const RafCampaignPostRequest = z.object({
  name: z.string().min(1).max(60),
  description: z.string().nullable().optional(),
  startDate: z.string().describe('ISO date string (YYYY-MM-DD)'),
  endDate: z.string().describe('ISO date string (YYYY-MM-DD)'),
  brand: z.nativeEnum(Brand),
  referrerDiscountId: z.string().uuid().nullable().optional(),
  refereeDiscountId: z.string().uuid().nullable().optional(),
  laravelId: z.number().int().nullable().optional(),
  referrerRewards: z.array(RafCampaignReferrerRewardPostRequest).optional(),
})

export type RafCampaignPostRequest = z.infer<typeof RafCampaignPostRequest>

export const BulkRafCampaignPostRequest = z.object({
  campaigns: z.array(RafCampaignPostRequest).max(20),
})

export type BulkRafCampaignPostRequest = z.infer<typeof BulkRafCampaignPostRequest>

export const BulkRafCampaignPostResponse = z.object({
  campaigns: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      startDate: z.string(),
      endDate: z.string(),
      laravelId: z.number().int().nullable(),
    }),
  ),
})

export type BulkRafCampaignPostResponse = z.infer<typeof BulkRafCampaignPostResponse>

/**
 * Points a brand's existing referral coupons at a campaign's referee discount, so customers who
 * already hold a code earn that campaign's perks instead of the ones their coupon was stamped with.
 *
 * A coupon records the offer its referee gets, and `getUserReferrals` reads that offer back to
 * decide which campaign a referral is paid from. Without this, a new campaign only ever reaches
 * people who sign up after it starts.
 */
export const RepointRafCouponsToCampaignPostRequest = z.object({
  brand: z.nativeEnum(Brand),
  /** Guards against repointing onto a campaign that has no perks configured yet. */
  requireRewardTiers: z.boolean().optional().default(true),
  dryRun: z.boolean().optional().default(false),
  /**
   * When set, repoints at most this many coupons in one call and returns `done: false` if more remain.
   * Lets the launch workflow move hundreds of thousands of coupons in bounded HTTP calls that stay
   * well under the internal ALB idle timeout. Absent means repoint everything in one UPDATE, which
   * is only safe for small coupon counts.
   */
  batchSize: z.number().int().positive().max(50_000).optional(),
})

export type RepointRafCouponsToCampaignPostRequest = z.infer<typeof RepointRafCouponsToCampaignPostRequest>

export const RepointRafCouponsToCampaignPostResponse = z.object({
  campaignId: z.string().uuid(),
  campaignName: z.string(),
  refereeDiscountId: z.string().uuid(),
  /**
   * Coupons pointing somewhere else, counted before anything was written.
   * In batch mode this is the count of remaining eligible rows before this batch ran, not after.
   */
  eligible: z.number().int(),
  /** Zero on a dry run, otherwise how many rows this call's update actually changed. */
  repointed: z.number().int(),
  /**
   * True when there is nothing left to repoint. Always true on a non-batch call (it moves everything)
   * and on a dry run that found no eligible coupons. In batch mode, false means "call again".
   */
  done: z.boolean(),
})

export type RepointRafCouponsToCampaignPostResponse = z.infer<typeof RepointRafCouponsToCampaignPostResponse>

export const GetRafCampaignsResponse = z.object({
  campaigns: z.array(RafCampaignSchema),
})

export type GetRafCampaignsResponse = z.infer<typeof GetRafCampaignsResponse>

export const RafCampaignIdPathParams = z.object({
  campaignId: z.string().uuid(),
})

export type RafCampaignIdPathParams = z.infer<typeof RafCampaignIdPathParams>

export const UserIdPathParams = z.object({
  userId: z.string().uuid(),
})

export type UserIdPathParams = z.infer<typeof UserIdPathParams>

export const RafCouponGetQuery = z.object({
  brand: z.nativeEnum(Brand),
})

export type RafCouponGetQuery = z.infer<typeof RafCouponGetQuery>

export const GetUserReferralsQuery = z.object({
  brand: z.nativeEnum(Brand),
})

export type GetUserReferralsQuery = z.infer<typeof GetUserReferralsQuery>

export const ReferralRafCampaignSchema = z.object({
  rafCampaignId: z.string().uuid().nullable(),
  rafCampaignName: z.string().min(1).max(60).nullable(),
})

export type ReferralRafCampaignSchema = z.infer<typeof ReferralRafCampaignSchema>

export const GetUserReferralsResponse = z.object({
  referrals: z.array(
    z.object({
      reward: z
        .object({
          rewardId: z.string().uuid(),
          discount: DiscountSchema,
          items: z.array(
            z.object({
              productVariantId: z.string().uuid(),
              quantity: z.number().int().nonnegative(),
            }),
          ),
        })
        .nullable(),
      referral: z
        .object({
          promotionUserId: z.string().uuid(),
          userId: z.string().uuid(),
          appliedAt: z.string(),
        })
        .merge(ReferralRafCampaignSchema)
        .nullable(),
    }),
  ),
})

export type GetUserReferralsResponse = z.infer<typeof GetUserReferralsResponse>

export const PromotionUsersRevertResponse = z.object({
  promotionUsersDeletedCount: z.number(),
})

export type PromotionUsersRevertResponse = z.infer<typeof PromotionUsersRevertResponse>

export type PromotionUserPathParams = z.infer<typeof PromotionUserPathParams>

export const PromotionUserPathParams = z.object({
  id: z.string().uuid(),
})

// ─── Promotion User Create ───────────────────────────────────────────────────
export type PromotionUserPostRequest = z.infer<typeof PromotionUserPostRequest>

export const PromotionUserPostRequest = z.object({
  id: z.string().uuid().optional(),
  promotionId: z.string().uuid(),
  userId: z.string().uuid(),
  type: z.nativeEnum(PromotionUsageType),
  discountId: z.string().uuid().nullable().optional(),
  context: z.nativeEnum(PromotionUsageContext).nullable().optional(),
  appliedAt: DateTimeSchema.nullable().optional(),
  createdAt: DateTimeSchema.nullable().optional(),
  updatedAt: DateTimeSchema.nullable().optional(),
})

export type PromotionUserPostResponse = z.infer<typeof PromotionUserPostResponse>

export const PromotionUserPostResponse = z.object({
  id: z.string().uuid(),
})

export const PromotionUserGetResponse = PromotionUserSchema.and(
  z.object({
    promotion: PromotionSchema.omit({ users: true }),
  }),
)
export type PromotionUserGetResponse = z.infer<typeof PromotionUserGetResponse>

// ─── Promotion User Filter ──────────────────────────────────────────────────
export type PromotionUsersGetRequest = z.infer<typeof PromotionUsersGetRequest>

export const PromotionUsersGetRequest = PaginationRequest.extend({
  userId: z.string().uuid().optional().describe('Filter records by user id'),
  includePromotion: QueryBooleanSchema.optional().describe('Include the related promotion record on each result'),
  orderBy: z.enum(['createdAt', 'updatedAt', 'appliedAt']).optional(),
})

export const PromotionUserResultSchema = PromotionUserSchema.extend({
  promotion: PromotionSchema.omit({ users: true }).optional(),
})

export type PromotionUserResultSchema = z.infer<typeof PromotionUserResultSchema>

export type PromotionUsersGetResponse = z.infer<typeof PromotionUsersGetResponse>

export const PromotionUsersGetResponse = PaginationResponse.extend({
  results: z.array(PromotionUserResultSchema),
})

// ─── Bulk Promotion User Upsert ─────────────────────────────────────────────
export type BulkPromotionUserPostRequest = z.infer<typeof BulkPromotionUserPostRequest>

export const BulkPromotionUserPostRequest = z.object({
  promotionUsers: z.array(PromotionUserPostRequest).max(1000),
})

export type BulkPromotionUserPostResponse = z.infer<typeof BulkPromotionUserPostResponse>

export const BulkPromotionUserPostResponse = z.object({
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  errors: z.array(
    z.object({
      index: z.number().int().nonnegative(),
      error: z.string(),
    }),
  ),
})

export type PromotionCategorySchema = z.infer<typeof PromotionCategorySchema>

export const PromotionCategorySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  createdAt: DateSchema,
  updatedAt: DateSchema,
  deletedAt: DateSchema,
})

export type PromotionCategoryGetResponse = z.infer<typeof PromotionCategoryGetResponse>

export const PromotionCategoryGetResponse = z.object({
  results: z.array(PromotionCategorySchema),
})

export const MigratePromotionsRequest = z.object({
  laravelId: z.number(),
  userId: z.string().uuid().optional(),
  coupon: z.string(),
  discountAmountType: z.enum(['percentage', 'dollar']),
  discountType: z.enum(['all', 'meals', 'treats', 'supplements']),
  discountAmount: z.number(),
  multiBoxDiscountEnabled: z.boolean(),
  multiBoxDiscounts: z.string().optional(),
  couponActive: z.coerce.date(),
  couponExpire: z.coerce.date(),
  categoryId: z.number(),
})

export type MigratePromotionsRequest = z.infer<typeof MigratePromotionsRequest>

export const MigrateMultiUsePromotionUserRequest = z.object({
  laravelId: z.number().int().positive().describe('multi_use_coupons_users.id'),
  laravelMultiCouponId: z.number().int().positive().describe('multi_use_coupons_users.multi_coupon_id'),
  userId: z
    .string()
    .uuid()
    .describe('Phoenix user id (multi_use_coupons_users.user_id converted via laravelUserIdToPhoenixId)'),
  userCreatedAt: z.coerce
    .date()
    .describe(
      'Phoenix user.date_created — used for created_at, updated_at, and applied_at on the resulting promotion_user row',
    ),
  coupon: z.string().describe('multi_use_coupons.coupon — required to resolve the discount signature'),
  discountAmountType: z.enum(['percentage', 'dollar']),
  discountType: z.enum(['all', 'meals', 'treats', 'supplements']),
  discountAmount: z.number(),
  multiBoxDiscountEnabled: z.boolean(),
  multiBoxDiscounts: z.string().optional(),
})

export type MigrateMultiUsePromotionUserRequest = z.infer<typeof MigrateMultiUsePromotionUserRequest>
